import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react';
import { foundation } from '../../../ui/tokens';
import { bannerToneColor } from '../../../ui/bannerStyles';
import { useResponsiveLayout } from '../../../hooks/ui/useResponsiveLayout';

const { color, spacePx, radii, fontSize, fontWeight, fontFamily } = foundation;

const TONE_ICON = { info: Info, success: CircleCheck, warning: TriangleAlert, error: CircleX };

/**
 * Calm banner (KAN-1173): white card, neutral hairline, one leading tone icon, title in the
 * default text colour, optional muted detail, optional blue text actions and a trailing dismiss.
 * Colour = the icon only.
 *
 * @param {object} props
 * @param {'info'|'success'|'warning'|'error'} [props.tone]
 * @param {React.ComponentType} [props.icon] overrides the tone icon (colour still from tone)
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.children] detail (muted, smaller)
 * @param {{label: string, onClick: Function, testId?: string, destructive?: boolean}} [props.action]
 * @param {Array<{label: string, onClick: Function, testId?: string, destructive?: boolean}>} [props.actions]
 * @param {Function} [props.onDismiss] renders a trailing close button
 * @param {string} [props.dismissLabel] aria-label of the close button
 * @param {string} [props.dismissTestId]
 * @param {'start'|'center'} [props.align] 'center' = the Log Job one-row look (8px / 16px padding, centred row)
 * @param {React.ReactNode} [props.below] rendered under the row, inside the card
 * @param {string} [props.testId]
 * @param {'alert'|'status'|'region'} [props.role]
 * @param {string} [props.ariaLabel]
 * @param {object} [props.style]
 */
export default function CalmBanner({
  tone = 'info', icon, title, children, action, actions, onDismiss, dismissLabel, dismissTestId,
  align = 'start', below, testId, role = 'status', ariaLabel, style,
}) {
  const { isTouch } = useResponsiveLayout();
  const Icon = icon || TONE_ICON[tone] || Info;
  const center = align === 'center';
  const actionList = actions || (action ? [action] : []);
  const edgeRight = Boolean(onDismiss) || actionList.length > 0;
  const hit = isTouch ? 44 : 32;
  const rowPadding = center
    ? `${spacePx[2]} ${edgeRight ? spacePx[1] : spacePx[4]} ${spacePx[2]} ${spacePx[4]}`
    : `${spacePx[3]} ${spacePx[4]}`;

  return (
    <div
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      data-tone={tone}
      style={{
        backgroundColor: color.neutral.surface,
        border: `1px solid ${color.neutral.border}`,
        borderRadius: radii.lg,
        fontFamily: fontFamily.sans,
        color: color.neutral.text,
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: center ? 'center' : 'flex-start',
          gap: spacePx[3],
          padding: rowPadding,
          minHeight: center ? 44 : undefined,
        }}
      >
        <Icon size={18} strokeWidth={2.25} aria-hidden data-testid="calm-banner-icon" style={{ flexShrink: 0, marginTop: center ? 0 : 1, color: bannerToneColor[tone] }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {title ? <div style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, lineHeight: 1.4 }}>{title}</div> : null}
          {children ? (
            <div style={{ marginTop: title ? 2 : 0, fontSize: fontSize.xs, color: color.neutral.textMuted, lineHeight: 1.45 }}>{children}</div>
          ) : null}
        </div>
        {actionList.map((a, i) => (
          <button
            key={a.testId || a.label || i}
            type="button"
            data-testid={a.testId}
            onClick={a.onClick}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              minHeight: hit,
              margin: center ? 0 : (isTouch ? '-12px -8px -12px 0' : '-6px 0'),
              padding: `0 ${spacePx[2]}`,
              fontFamily: 'inherit',
              fontSize: fontSize.sm,
              fontWeight: fontWeight.semibold,
              color: a.destructive ? color.semantic.danger : color.brand.text,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {a.label}
          </button>
        ))}
        {onDismiss ? (
          <button
            type="button"
            data-testid={dismissTestId}
            aria-label={dismissLabel}
            onClick={onDismiss}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
              width: hit,
              height: hit,
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color.neutral.textMuted,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <X size={18} strokeWidth={2.25} aria-hidden />
          </button>
        ) : null}
      </div>
      {below}
    </div>
  );
}
